import React, { useEffect, useState, useCallback } from "react";
import { rateLimitsAPI } from "../../services/api/admin";

const POLL_INTERVAL_MS = 15000;

function pct(used, cap) {
  if (!cap || cap <= 0) return 0;
  const p = (Number(used || 0) / Number(cap)) * 100;
  if (!isFinite(p) || p < 0) return 0;
  return Math.min(p, 100);
}

function barColor(p) {
  if (p >= 90) return "bg-red-500";
  if (p >= 70) return "bg-yellow-500";
  return "bg-green-500";
}

function UtilizationBar({ label, used, cap }) {
  const p = pct(used, cap);
  return (
    <div className="min-w-[140px]">
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
        <span>{label}</span>
        <span>
          {used ?? 0}/{cap ?? "—"}
        </span>
      </div>
      <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${barColor(p)}`} style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

export default function AdminRateLimits() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [editingId, setEditingId] = useState(null);
  const [editRpm, setEditRpm] = useState("");
  const [editTpm, setEditTpm] = useState("");
  const [saving, setSaving] = useState({});

  const showMsg = (type, text) => {
    setMsg({ type, text });
    if (text) setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const load = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const data = await rateLimitsAPI.list();
      setRows(data || []);
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Failed to load rate limits");
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const interval = setInterval(() => load(false), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditRpm(String(row.rpm ?? ""));
    setEditTpm(String(row.tpm ?? ""));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditRpm("");
    setEditTpm("");
  };

  const saveEdit = async (row) => {
    const rpm = parseInt(editRpm, 10);
    const tpm = parseInt(editTpm, 10);
    if (isNaN(rpm) || rpm <= 0 || isNaN(tpm) || tpm <= 0) {
      showMsg("error", "RPM and TPM must be positive numbers");
      return;
    }
    setSaving((prev) => ({ ...prev, [row.id]: true }));
    try {
      await rateLimitsAPI.update(row.id, { rpm, tpm });
      showMsg("success", `${row.vendor} / ${row.lane} updated`);
      cancelEdit();
      await load(false);
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Failed to update rate limit");
    } finally {
      setSaving((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const toggleEnabled = async (row) => {
    setSaving((prev) => ({ ...prev, [row.id]: true }));
    try {
      await rateLimitsAPI.update(row.id, { enabled: !row.enabled });
      showMsg("success", `${row.vendor} / ${row.lane} ${!row.enabled ? "enabled" : "disabled"}`);
      await load(false);
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Failed to update rate limit");
    } finally {
      setSaving((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Rate Limits</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Per-vendor/lane LLM request caps — proactively throttles dispatch before the vendor rejects a call.
          </p>
        </div>
      </div>

      {msg.text && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm ${
            msg.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-600 border border-red-100"
          }`}
        >
          {msg.text}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">No rate limits found.</div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Vendor</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Lane</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Model</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">RPM / TPM</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Utilization</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Pending</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Enabled</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((row) => {
                const isEditing = editingId === row.id;
                const isSaving = !!saving[row.id];
                const util = row.utilization || {};

                return (
                  <tr key={row.id} className="hover:bg-muted/50 align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-foreground">{row.vendor}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">{row.lane}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">{row.model || "—"}</td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            value={editRpm}
                            onChange={(e) => setEditRpm(e.target.value)}
                            className="w-20 rounded border border-input px-2 py-1 text-sm"
                            placeholder="RPM"
                          />
                          <input
                            type="number"
                            min="1"
                            value={editTpm}
                            onChange={(e) => setEditTpm(e.target.value)}
                            className="w-24 rounded border border-input px-2 py-1 text-sm"
                            placeholder="TPM"
                          />
                        </div>
                      ) : (
                        <span>
                          {row.rpm} / {row.tpm}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-2">
                        <UtilizationBar label="RPM" used={util.rpm_used} cap={util.rpm_cap} />
                        <UtilizationBar label="TPM" used={util.tpm_used} cap={util.tpm_cap} />
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                      Pending: {row.pending_count ?? 0}
                      {row.pending_count > 0 && (
                        <div className="text-xs text-muted-foreground">
                          oldest {row.oldest_pending_age_s ?? "—"}s
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <label className="flex items-center gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={!!row.enabled}
                          disabled={isSaving}
                          onChange={() => toggleEnabled(row)}
                          className="h-4 w-4 rounded border-input"
                        />
                        {row.enabled ? "Enabled" : "Disabled"}
                      </label>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => saveEdit(row)}
                            disabled={isSaving}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {isSaving ? "Saving..." : "Save"}
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={isSaving}
                            className="px-3 py-1.5 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted/70 disabled:opacity-40"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(row)}
                          className="px-3 py-1.5 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted/70"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
